// Utilidad centralizada para manejar iconos de tipos de artefactos
import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { renderIconifyPreview } from './iconifyUtils';
import { getApiBaseUrl } from '../config';
import { Icon } from '@iconify/react';
import {
  Lightbulb as LightbulbIcon,
  BlindsTwoTone as BlindsTwoToneIcon,
  TouchApp as TouchAppIcon,
  PowerSharp as PowerIcon,
  AcUnit as AcUnitIcon,
  Kitchen as KitchenIcon,
  LocalLaundryService as LaundryIcon,
  Pool as PoolIcon,
  WaterDrop as WaterDropIcon,
  Tv as TvIcon,
  Devices as DevicesIcon,
  ElectricMeterOutlined as ElectricMeterOutlinedIcon,
  ElevatorOutlined as ElevatorOutlinedIcon,
  FireplaceOutlined as FireplaceOutlinedIcon
} from '@mui/icons-material';
// Los íconos PNG ahora están en el backend y se cargan dinámicamente
// Mapeo de identificadores PNG a nombres de archivo (servidos desde el backend)
const pngIconFiles: Record<string, string> = {
  'bulb.png': 'bulb.png',
  'plug.png': 'plug.png',
  'blind.png': 'blind.png',
  'fridge.png': 'fridge.png',
  'water-heater.png': 'water-heater.png',
  'water_pump.png': 'water_pump.png',
  'air-conditioning_1.png': 'air-conditioning_1.png',
  'motor_air_condition.png': 'motor_air_condition.png',
  'electric_panel.png': 'electric_panel.png',
  'power-meter.png': 'power-meter.png',
  'electricity-tower.png': 'electricity-tower.png',
};

// Función para obtener la URL del ícono desde el backend
const getPngIconUrl = (filename: string): string => {
  return `${getApiBaseUrl()}/iconify/${filename}`;
};

// Mapeo de identificadores Material-UI a componentes
const muiIcons: Record<string, React.ComponentType<any>> = {
  'Lightbulb': LightbulbIcon,
  'BlindsTwoTone': BlindsTwoToneIcon,
  'TouchApp': TouchAppIcon,
  'PowerSharp': PowerIcon,
  'AcUnit': AcUnitIcon,
  'Kitchen': KitchenIcon,
  'LocalLaundryService': LaundryIcon,
  'Pool': PoolIcon,
  'WaterDrop': WaterDropIcon,
  'Tv': TvIcon,
  'Devices': DevicesIcon,
  'ElectricMeterOutlined': ElectricMeterOutlinedIcon,
  'ElevatorOutlined': ElevatorOutlinedIcon,
  'FireplaceOutlined': FireplaceOutlinedIcon,
};

// Filtros CSS para colores específicos (legacy - para iconos locales con colores predefinidos)
const colorFilters: Record<string, string> = {
  'bulb.png': 'brightness(0) saturate(100%) invert(67%) sepia(95%) saturate(7499%) hue-rotate(90deg) brightness(101%) contrast(101%)', // Verde #4CAF50
  'plug.png': 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(7479%) hue-rotate(346deg) brightness(96%) contrast(96%)', // Rojo #F44336
  'blind.png': 'brightness(0) saturate(100%) invert(80%)', // Gris claro #CCCCCC
  'default': 'brightness(0) saturate(100%) invert(58%) sepia(96%) saturate(7499%) hue-rotate(200deg) brightness(101%) contrast(101%)', // Azul #5C9EFF
  'power-meter.png': 'brightness(0) saturate(100%) invert(100%)', // Blanco #FFFFFF
};

// Función para generar filtro CSS dinámico desde un color hex
// Convierte un color hex a un filtro CSS que puede aplicarse a imágenes PNG
const hexToFilter = (hex: string): string => {
  // Remover # si existe
  hex = hex.replace('#', '');
  
  // Convertir hex a RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calcular filtro CSS usando la fórmula estándar
  // Esta fórmula convierte un color RGB a un filtro CSS que puede aplicarse a imágenes en escala de grises
  // Fuente: https://stackoverflow.com/questions/42966641/how-to-transform-black-into-any-given-color-using-only-css-filters
  
  // Para colores específicos, necesitamos calcular los valores del filtro
  // Usamos una aproximación basada en la fórmula estándar
  
  // Calcular valores normalizados (0-1)
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  
  // Calcular valores del filtro
  // Fórmula simplificada para convertir RGB a filtro CSS
  const brightness = (rNorm + gNorm + bNorm) / 3;
  const saturation = Math.max(rNorm, gNorm, bNorm) - Math.min(rNorm, gNorm, bNorm);
  
  // Calcular hue (tono)
  let hue = 0;
  if (saturation > 0) {
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const delta = max - min;
    
    if (max === rNorm) {
      hue = ((gNorm - bNorm) / delta) % 6;
    } else if (max === gNorm) {
      hue = (bNorm - rNorm) / delta + 2;
    } else {
      hue = (rNorm - gNorm) / delta + 4;
    }
    hue = hue * 60;
    if (hue < 0) hue += 360;
  }
  
  // Construir filtro CSS
  // Usamos una aproximación que funciona bien para la mayoría de colores
  const sepia = saturation * 100;
  const saturate = 100 + (saturation * 100);
  const hueRotate = hue;
  const brightnessValue = brightness * 100;
  
  return `brightness(0) saturate(100%) invert(${brightnessValue}%) sepia(${sepia}%) saturate(${saturate}%) hue-rotate(${hueRotate}deg) brightness(${brightnessValue}%) contrast(100%)`;
};

// Función mejorada para generar filtro CSS desde hex (más precisa)
export const hexToCssFilter = (hex: string): string => {
  // Remover # si existe
  hex = hex.replace('#', '');
  
  if (hex.length !== 6) {
    // Si no es válido, usar filtro por defecto
    return colorFilters['default'];
  }
  
  // Usar una librería de cálculo más precisa
  // Para simplificar, usamos valores precalculados para colores comunes
  const colorMap: Record<string, string> = {
    '#5C9EFF': 'brightness(0) saturate(100%) invert(58%) sepia(96%) saturate(7499%) hue-rotate(200deg) brightness(101%) contrast(101%)',
    '#4CAF50': 'brightness(0) saturate(100%) invert(67%) sepia(95%) saturate(7499%) hue-rotate(90deg) brightness(101%) contrast(101%)',
    '#F44336': 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(7479%) hue-rotate(346deg) brightness(96%) contrast(96%)',
    '#FFC107': 'brightness(0) saturate(100%) invert(77%) sepia(95%) saturate(7499%) hue-rotate(1deg) brightness(101%) contrast(101%)',
    '#FF9800': 'brightness(0) saturate(100%) invert(66%) sepia(95%) saturate(7499%) hue-rotate(1deg) brightness(101%) contrast(101%)',
    '#9C27B0': 'brightness(0) saturate(100%) invert(40%) sepia(95%) saturate(7499%) hue-rotate(270deg) brightness(101%) contrast(101%)',
    '#E91E63': 'brightness(0) saturate(100%) invert(35%) sepia(95%) saturate(7499%) hue-rotate(320deg) brightness(101%) contrast(101%)',
    '#00BCD4': 'brightness(0) saturate(100%) invert(70%) sepia(95%) saturate(7499%) hue-rotate(180deg) brightness(101%) contrast(101%)',
    '#FFFFFF': 'brightness(0) saturate(100%) invert(100%)',
    '#000000': 'brightness(0) saturate(100%)',
  };
  
  const upperHex = '#' + hex.toUpperCase();
  if (colorMap[upperHex]) {
    return colorMap[upperHex];
  }
  
  // Si no está en el mapa, calcular dinámicamente
  return hexToFilter('#' + hex);
};

// Componente para renderizar iconos de Iconify con color personalizado
const IconifyIconWithColor: React.FC<{
  iconUrl: string;
  iconId: string;
  size: number;
  color: string;
}> = ({ iconUrl, iconId, size, color }) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState(true); // Por defecto usar Icon de Iconify (siempre funciona)

  useEffect(() => {
    // Siempre mostrar primero con Icon de Iconify (funciona inmediatamente)
    // Luego intentar cargar desde el servidor para mejor calidad
    fetch(iconUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error('SVG no encontrado en servidor');
        }
        return response.text();
      })
      .then(svg => {
        // Aplicar el color al SVG
        let coloredSvg = svg;
        
        // Reemplazar fill con el color seleccionado
        coloredSvg = coloredSvg.replace(/fill="[^"]*"/g, (match) => {
          if (match === 'fill="none"') return match;
          return `fill="${color}"`;
        });
        
        // Si no tiene fill, agregarlo
        if (!coloredSvg.includes('fill=')) {
          coloredSvg = coloredSvg.replace(/<svg([^>]*)>/, `<svg$1 fill="${color}">`);
        }
        
        // Hacer lo mismo con stroke
        if (coloredSvg.includes('stroke=')) {
          coloredSvg = coloredSvg.replace(/stroke="[^"]*"/g, (match) => {
            if (match === 'stroke="none"') return match;
            return `stroke="${color}"`;
          });
        }
        
        // Asegurar tamaño correcto
        coloredSvg = coloredSvg.replace(/<svg([^>]*)>/, `<svg$1 width="${size}" height="${size}">`);
        
        setSvgContent(coloredSvg);
        setUseFallback(false); // Cambiar a usar SVG del servidor
      })
      .catch(() => {
        // Si falla, mantener el fallback con Icon de Iconify
        // useFallback ya está en true
      });
  }, [iconUrl, iconId, size, color]);

  // Siempre mostrar algo: primero Icon de Iconify, luego SVG del servidor si está disponible
  if (useFallback || !svgContent) {
    // Usar componente Icon de Iconify directamente (siempre funciona)
    return (
      <Icon 
        icon={iconId} 
        width={size} 
        height={size}
        style={{ color: color }}
      />
    );
  }

  // Si tenemos SVG del servidor, usarlo (mejor calidad)
  return (
    <Box
      component="span"
      dangerouslySetInnerHTML={{ __html: svgContent }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        '& svg': {
          width: size,
          height: size,
          color: color,
        }
      }}
    />
  );
};

// Función para renderizar icono según identificador (tamaño grande - 48px)
export const renderArtifactIcon = (
  icono: string | null | undefined,
  nombreArtefacto: string | null | undefined,
  size: 'large' | 'small' = 'large',
  color?: string | null
): React.ReactNode | null => {
  const iconSize = size === 'large' ? 48 : 18;
  const defaultColor = '#5C9EFF';
  const iconColor = color || defaultColor;

  // Si hay icono guardado, usarlo
  if (icono) {
    // Verificar si es PNG que ahora está en el backend
    if (pngIconFiles[icono]) {
      // Usar el color proporcionado (o el color guardado) en lugar del filtro hardcodeado
      const filter = hexToCssFilter(iconColor);
      return (
        <Box
          component="img"
          loading="lazy"
          src={getPngIconUrl(icono)}
          alt={nombreArtefacto || 'Artefacto'}
          sx={{
            width: iconSize,
            height: iconSize,
            objectFit: 'contain',
            filter: filter,
          }}
        />
      );
    }

    // Verificar si es Material-UI (formato: "MaterialUI:IconName")
    if (icono.startsWith('MaterialUI:')) {
      const iconName = icono.replace('MaterialUI:', '');
      const IconComponent = muiIcons[iconName];
      if (IconComponent) {
        return <IconComponent sx={{ fontSize: iconSize, color: iconColor }} />;
      }
    }

    // Verificar si es CustomIcon (formato: "CustomIcon:filename.ext") - iconos descargados por usuario
    if (icono.startsWith('CustomIcon:')) {
      const filename = icono.replace('CustomIcon:', '');
      const iconUrl = `${getApiBaseUrl()}/iconify/${filename}`;
      const isPng = filename.toLowerCase().endsWith('.png');
      const isGif = filename.toLowerCase().endsWith('.gif');
      const isJpg = filename.toLowerCase().endsWith('.jpg') || filename.toLowerCase().endsWith('.jpeg');
      
      if (isPng || isGif || isJpg) {
        // Es imagen binaria: usar filtro CSS para PNG/GIF/JPG
        const filter = hexToCssFilter(iconColor);
        return (
          <Box
            component="img"
            loading="lazy"
            src={iconUrl}
            alt={nombreArtefacto || 'Artefacto'}
            sx={{
              width: iconSize,
              height: iconSize,
              objectFit: 'contain',
              filter: filter,
            }}
          />
        );
      } else {
        // Es SVG: usar componente para aplicar color
        return <IconifyIconWithColor iconUrl={iconUrl} iconId={filename} size={iconSize} color={iconColor} />;
      }
    }

    // Verificar si es Iconify (formato: "Iconify:mdi:chef-hat") - iconos de Iconify API
    if (icono.startsWith('Iconify:')) {
      const iconId = icono.replace('Iconify:', '');
      
      // Buscar en la lista de iconos descargados
      // El backend ahora retorna solo filename, así que buscamos por filename convertido
      const filenameFromId = iconId.replace(/:/g, '-');
      const downloadedIcon = downloadedIconifyIcons.find(icon => {
        const iconFilename = icon.filename.replace(/\.(svg|png|gif|jpg|jpeg)$/i, '');
        return iconFilename === filenameFromId;
      });
      const isPng = downloadedIcon ? downloadedIcon.filename.toLowerCase().endsWith('.png') : false;
      const isGif = downloadedIcon ? downloadedIcon.filename.toLowerCase().endsWith('.gif') : false;
      const isJpg = downloadedIcon ? (downloadedIcon.filename.toLowerCase().endsWith('.jpg') || downloadedIcon.filename.toLowerCase().endsWith('.jpeg')) : false;
      
      // Construir nombres de archivo
      const pngFilename = iconId.replace(/:/g, '-') + '.png';
      const svgFilename = iconId.replace(/:/g, '-') + '.svg';
      const gifFilename = iconId.replace(/:/g, '-') + '.gif';
      const jpgFilename = iconId.replace(/:/g, '-') + '.jpg';
      const pngUrl = `${getApiBaseUrl()}/iconify/${pngFilename}`;
      const svgUrl = `${getApiBaseUrl()}/iconify/${svgFilename}`;
      const gifUrl = `${getApiBaseUrl()}/iconify/${gifFilename}`;
      const jpgUrl = `${getApiBaseUrl()}/iconify/${jpgFilename}`;
      
      if (isPng || isGif || isJpg) {
        // Es imagen binaria descargada: renderizar directamente con filtro CSS
        const filter = hexToCssFilter(iconColor);
        const imageUrl = isPng ? pngUrl : (isGif ? gifUrl : jpgUrl);
        return (
          <Box
            component="img"
            loading="lazy"
            src={imageUrl}
            alt={nombreArtefacto || 'Artefacto'}
            sx={{
              width: iconSize,
              height: iconSize,
              objectFit: 'contain',
              filter: filter,
            }}
            onError={(e) => {
              // Si falla, intentar SVG como fallback
              console.log('[renderArtifactIcon] Imagen no encontrada, intentando SVG:', imageUrl);
              const target = e.currentTarget as HTMLImageElement;
              target.src = svgUrl;
            }}
          />
        );
      } else {
        // Es SVG: usar componente IconifyIconWithColor
        return <IconifyIconWithColor iconUrl={svgUrl} iconId={iconId} size={iconSize} color={iconColor} />;
      }
    }
  }

  // Fallback: usar lógica automática basada en nombre
  if (nombreArtefacto) {
    return getAutoIconForArtefacto(nombreArtefacto, iconSize);
  }

  return null;
};

// Función para renderizar icono pequeño (18px) - versión optimizada
export const renderArtifactIconSmall = (
  icono: string | null | undefined,
  nombreArtefacto: string | null | undefined,
  color?: string | null
): React.ReactNode | null => {
  return renderArtifactIcon(icono, nombreArtefacto, 'small', color);
};

// Función automática de fallback basada en nombre (tamaño configurable)
const getAutoIconForArtefacto = (nombreArtefacto: string, size: number = 48): React.ReactNode => {
  const nombreLower = nombreArtefacto.toLowerCase();
  const defaultColor = '#5C9EFF';
  const defaultFilter = 'brightness(0) saturate(100%) invert(58%) sepia(96%) saturate(7499%) hue-rotate(200deg) brightness(101%) contrast(101%)';

  // Calefacción
  if (nombreLower.includes('calefacc') || nombreLower.includes('calefacción')) {
    return <FireplaceOutlinedIcon sx={{ fontSize: size, color: defaultColor }} />;
  }

  // Motor Aire Acondicionado
  if (nombreLower.includes('motor') && (nombreLower.includes('aire') || nombreLower.includes('acondicionado'))) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('motor_air_condition.png')}
        alt="Motor Aire Acondicionado"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: defaultFilter,
        }}
      />
    );
  }

  // Climatización / Aire Acondicionado
  if (nombreLower.includes('climatiz') || nombreLower.includes('aire') || nombreLower.includes('acondicionado')) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('air-conditioning_1.png')}
        alt="Aire Acondicionado"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: defaultFilter,
        }}
      />
    );
  }

  // Cocina
  if (nombreLower.includes('cocina') || nombreLower.includes('horno') || nombreLower.includes('microondas')) {
    return <KitchenIcon sx={{ fontSize: size, color: defaultColor }} />;
  }

  // Lavadero
  if (nombreLower.includes('lavadero') || nombreLower.includes('lavadora') || nombreLower.includes('secadora')) {
    return <LaundryIcon sx={{ fontSize: size, color: defaultColor }} />;
  }

  // Piscina y Jardín
  if (nombreLower.includes('piscina') || nombreLower.includes('jardin') || nombreLower.includes('jardín')) {
    return <PoolIcon sx={{ fontSize: size, color: defaultColor }} />;
  }

  // Bomba de Agua
  if (nombreLower.includes('bomba') && nombreLower.includes('agua')) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('water_pump.png')}
        alt="Bomba de Agua"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: defaultFilter,
        }}
      />
    );
  }

  // Termontanque / Agua
  if (nombreLower.includes('termontanque') || nombreLower.includes('termotanque') || nombreLower.includes('calentador')) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('water-heater.png')}
        alt="Termotanque"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: defaultFilter,
        }}
      />
    );
  }

  // Entretenimiento
  if (nombreLower.includes('entretenimiento') || nombreLower.includes('tv') || nombreLower.includes('televisor')) {
    return <TvIcon sx={{ fontSize: size, color: defaultColor }} />;
  }

  // Refrigeración / Heladera
  if (nombreLower.includes('refriger') || nombreLower.includes('heladera') || nombreLower.includes('freezer')) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('fridge.png')}
        alt="Heladera"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: defaultFilter,
        }}
      />
    );
  }

  // Tablero
  if (nombreLower.includes('tablero')) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('electric_panel.png')}
        alt="Tablero"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: defaultFilter,
        }}
      />
    );
  }

  // Medidores de Consumo
  if (nombreLower.includes('medidor') || nombreLower.includes('consumo') || nombreLower.includes('medidores')) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('power-meter.png')}
        alt="Consumo Total"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: colorFilters['power-meter.png'],
        }}
      />
    );
  }

  // Ascensor
  if (nombreLower.includes('ascensor') || nombreLower.includes('elevador')) {
    return <ElevatorOutlinedIcon sx={{ fontSize: size, color: defaultColor }} />;
  }

  // Conexión de Grid
  if (nombreLower.includes('grid') || nombreLower.includes('conexion') || nombreLower.includes('conexión') || nombreLower.includes('red')) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('electricity-tower.png')}
        alt="Torre eléctrica"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: defaultFilter,
        }}
      />
    );
  }

  // Luces
  if (nombreLower === 'luces' || nombreLower.includes('luz')) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('bulb.png')}
        alt="Luz"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: colorFilters['bulb.png'],
        }}
      />
    );
  }

  // Enchufes
  if (nombreLower === 'enchufes' || nombreLower.includes('enchufe')) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('plug.png')}
        alt="Enchufe"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: colorFilters['plug.png'],
        }}
      />
    );
  }

  // Cortinas
  if (nombreLower === 'cortinas' || nombreLower.includes('cortina')) {
    return (
      <Box
        component="img"
        loading="lazy"
        src={getPngIconUrl('blind.png')}
        alt="Cortina"
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          filter: colorFilters['blind.png'],
        }}
      />
    );
  }

  // Persianas / Portones
  if (nombreLower === 'persianas' || nombreLower.includes('persiana') || 
      nombreLower === 'portones' || nombreLower.includes('porton') || nombreLower.includes('portón')) {
    return <TouchAppIcon sx={{ fontSize: size, color: '#2391FF' }} />;
  }

  // Por defecto
  return <DevicesIcon sx={{ fontSize: size, color: defaultColor }} />;
};

// Catálogo de iconos disponibles para el selector
export interface IconOption {
  id: string;
  name: string;
  type: 'png' | 'mui' | 'iconify';
  preview: React.ReactNode;
}

// Estado para almacenar iconos descargados del servidor
// Los iconos descargados pueden tener icon_id (Iconify) o solo filename (URLs personalizadas)
let downloadedIconifyIcons: Array<{ icon_id?: string; filename: string }> = [];
let downloadedIconsLoaded = false;

// Función para cargar iconos descargados del servidor
export const loadDownloadedIconifyIcons = async (force: boolean = false): Promise<void> => {
  if (downloadedIconsLoaded && !force) return;
  
  try {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const response = await fetch(`${getApiBaseUrl()}/iconify/list`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.icons) {
        // El backend retorna objetos con filename, pero el frontend espera icon_id opcional
        // Para iconos descargados desde URL, solo tenemos filename
        // Para iconos de Iconify, podemos extraer icon_id del filename si tiene formato "collection-icon-name.svg"
        downloadedIconifyIcons = data.icons.map((icon: any) => {
          // Si el filename tiene formato de Iconify (ej: "mdi-chef-hat.svg"), extraer icon_id
          const filenameWithoutExt = icon.filename.replace(/\.(svg|png|gif|jpg|jpeg)$/i, '');
          // Verificar si parece un icono de Iconify (tiene guiones y podría tener formato "collection-icon")
          const parts = filenameWithoutExt.split('-');
          if (parts.length >= 2 && !filenameWithoutExt.includes('_')) {
            // Podría ser un icono de Iconify, intentar reconstruir icon_id
            const possibleIconId = filenameWithoutExt.replace(/-/g, ':');
            return {
              filename: icon.filename,
              icon_id: possibleIconId, // Opcional, para compatibilidad
              ...icon // Incluir size, modified, file_type si existen
            };
          }
          // Para otros iconos (descargados desde URL), solo filename
          return {
            filename: icon.filename,
            ...icon // Incluir size, modified, file_type si existen
          };
        });
        downloadedIconsLoaded = true;
        console.log('[loadDownloadedIconifyIcons] Iconos cargados:', downloadedIconifyIcons.length);
      }
    }
  } catch (error) {
    console.error('Error cargando iconos descargados:', error);
  }
};

// Función para forzar recarga de iconos descargados
export const refreshDownloadedIconifyIcons = async (): Promise<void> => {
  downloadedIconsLoaded = false;
  downloadedIconifyIcons = []; // Limpiar antes de recargar
  await loadDownloadedIconifyIcons(true); // Forzar recarga
  console.log('[refreshDownloadedIconifyIcons] Iconos recargados:', downloadedIconifyIcons.length);
};

// Generar catálogo de iconos disponibles (locales + descargados)
export const getAvailableIcons = (iconifyResults?: Array<{ name: string; displayName?: string }>): IconOption[] => {
  const icons: IconOption[] = [];

  // Agregar PNG que ahora están en el backend
  Object.keys(pngIconFiles).forEach((key) => {
    const filter = colorFilters[key] || colorFilters['default'];
    icons.push({
      id: key,
      name: key.replace('.png', '').replace(/_/g, ' ').replace(/-/g, ' '),
      type: 'png',
      preview: (
        <Box
          component="img"
          src={getPngIconUrl(key)}
          alt={key}
          sx={{
            width: 24,
            height: 24,
            objectFit: 'contain',
            filter: filter,
          }}
        />
      ),
    });
  });

  // Agregar Material-UI icons
  Object.keys(muiIcons).forEach((key) => {
    const IconComponent = muiIcons[key];
    icons.push({
      id: `MaterialUI:${key}`,
      name: key,
      type: 'mui',
      preview: <IconComponent sx={{ fontSize: 24, color: '#5C9EFF' }} />,
    });
  });

  // Agregar iconos descargados de Iconify (como iconos locales)
  // El backend ahora retorna solo filename, sin icon_id
  downloadedIconifyIcons.forEach((icon) => {
    const filename = icon.filename;
    
    // Evitar duplicados: si el PNG ya está en pngIconFiles (íconos locales movidos al backend),
    // no agregarlo de nuevo como descargado
    if (pngIconFiles[filename]) {
      return; // Saltar este ícono, ya está incluido como local
    }
    
    const isPng = filename.toLowerCase().endsWith('.png');
    const isGif = filename.toLowerCase().endsWith('.gif');
    const isJpg = filename.toLowerCase().endsWith('.jpg') || filename.toLowerCase().endsWith('.jpeg');
    const iconUrl = `${getApiBaseUrl()}/iconify/${filename}`;
    
    // Extraer nombre limpio del filename (sin extensión)
    // El filename ahora es el nombre que el usuario proporcionó
    const nameWithoutExt = filename.replace(/\.(svg|png|gif|jpg|jpeg)$/i, '');
    let cleanName = nameWithoutExt.replace(/-/g, ' ').replace(/_/g, ' ');
    
    // Si el nombre contiene "material-symbols" o similar, extraer solo la parte útil
    if (cleanName.includes('material-symbols') || cleanName.includes('material-symbol')) {
      const parts = cleanName.split(/[-:]/);
      const skipWords = ['material', 'symbol', 'symbols', 'outline', 'filled', 'round', 'sharp', 'thin', 'light', 'solid', 'regular', 'bold'];
      cleanName = parts.find(part => part && !skipWords.includes(part.toLowerCase())) || cleanName;
    }
    
    // Remover palabras comunes que no aportan información
    cleanName = cleanName
      .replace(/\bmaterial\s*symbols?\b/gi, '')
      .replace(/\boutline\b/gi, '')
      .replace(/\bfilled\b/gi, '')
      .replace(/\bround\b/gi, '')
      .replace(/\bsharp\b/gi, '')
      .replace(/\bthin\b/gi, '')
      .replace(/\blight\b/gi, '')
      .replace(/\bsolid\b/gi, '')
      .replace(/\bregular\b/gi, '')
      .replace(/\bbold\b/gi, '')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Limpiar espacios múltiples
    cleanName = cleanName.replace(/\s+/g, ' ').trim();
    
    // Si quedó vacío, usar el filename sin extensión
    if (!cleanName || cleanName.length < 1) {
      cleanName = nameWithoutExt;
    }
    // Determinar tipo según extensión
    let iconType: 'png' | 'iconify' = 'iconify';
    if (isPng || isGif || isJpg) {
      iconType = 'png';
    }
    
    icons.push({
      id: `CustomIcon:${filename}`, // Usar formato CustomIcon:filename
      name: cleanName,
      type: iconType,
      preview: (isPng || isGif || isJpg) ? (
        <Box
          component="img"
          src={iconUrl}
          alt={filename}
          sx={{
            width: 24,
            height: 24,
            objectFit: 'contain',
            filter: hexToCssFilter('#5C9EFF'), // Color azul por defecto para imágenes binarias
          }}
        />
      ) : (
        <Box
          component="img"
          src={iconUrl}
          alt={filename}
          sx={{
            width: 24,
            height: 24,
            objectFit: 'contain',
            color: '#5C9EFF', // Color azul por defecto para SVGs
          }}
        />
      ),
    });
  });

  // Agregar resultados de Iconify si se proporcionan (búsqueda nueva)
  if (iconifyResults && iconifyResults.length > 0) {
    iconifyResults.forEach((icon) => {
      // Solo agregar si no está ya en la lista de descargados
      // Verificar por icon_id (para iconos de Iconify) o por filename
      const alreadyDownloaded = downloadedIconifyIcons.some(di => 
        di.icon_id === icon.name || di.filename === `${icon.name.replace(':', '-')}.svg`
      );
      if (!alreadyDownloaded) {
        // Usar displayName si está disponible (ya viene limpio de iconifyUtils)
        let cleanName = icon.displayName || icon.name.split(':')[1]?.replace(/-/g, ' ') || icon.name;
        // Asegurar que esté limpio (por si acaso)
        cleanName = cleanName
          .replace(/\bmaterial\s+symbols?\b/gi, '')
          .replace(/\boutline\b/gi, '')
          .replace(/\bfilled\b/gi, '')
          .replace(/\bround\b/gi, '')
          .replace(/\bsharp\b/gi, '')
          .replace(/\bthin\b/gi, '')
          .replace(/\s+/g, ' ')
          .trim();
        if (!cleanName || cleanName.length < 2) {
          cleanName = icon.name.split(':')[1]?.replace(/-/g, ' ') || icon.name;
        }
        icons.push({
          id: `Iconify:${icon.name}`,
          name: cleanName,
          type: 'iconify',
          preview: renderIconifyPreview(icon.name, 24),
        });
      }
    });
  }

  return icons;
};

// Función para obtener solo iconos locales (PNG, Material-UI, y descargados de Iconify)
// NO incluye resultados de búsqueda
export const getLocalIconsOnly = (): IconOption[] => {
  const icons = getAvailableIcons(); // Sin parámetros = solo locales
  console.log('[getLocalIconsOnly] Total iconos locales:', icons.length, 'Iconos descargados:', downloadedIconifyIcons.length);
  return icons;
};

// Función para obtener solo iconos sugeridos de búsqueda (que NO están descargados)
export const getSuggestedIconsOnly = (iconifyResults: Array<{ name: string; displayName?: string }>): IconOption[] => {
  if (!iconifyResults || iconifyResults.length === 0) {
    return [];
  }
  
  // Obtener todos los iconos (locales + búsqueda)
  const allIcons = getAvailableIcons(iconifyResults);
  
  // Filtrar solo los de búsqueda que NO están descargados
  return allIcons.filter(icon => {
    if (icon.type !== 'iconify') return false;
    const iconId = icon.id.replace('Iconify:', '');
    // Verificar si NO está en la lista de descargados
    // Verificar por icon_id (para iconos de Iconify) o por filename
    return !downloadedIconifyIcons.some(di => 
      di.icon_id === iconId || di.filename === `${iconId.replace(':', '-')}.svg`
    );
  });
};

